import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import distributorService from '../services/distributorService';
import useShippingRequest from '../hooks/useShippingRequest';
import { useLocale } from '../i18n/LanguageContext';
import { validBalance } from './distributor-cash/cashContract';
import { usd } from './distributor-cash/cashMoney';
import StatusBadge from './StatusBadge';
import OrderDetailsModal from '../features/orders/OrderDetailsModal';
import { distributorHoldLabel } from '../features/orders/DistributorOrderDetails';
import DistributorOperationalNotices from './distributor/DistributorOperationalNotices';
import DistributorOverviewActivity from './distributor/DistributorOverviewActivity';
import '../styles/distributor-overview.css';

const metrics = [
  ['Customers', 'clinics', 'home_health', '/distributor/customers'],
  ['Open orders', 'openOrders', 'order_approve', '/distributor/orders'],
  [
    'IVRs needing review',
    'needsReviewIvrs',
    'call_quality',
    '/distributor/ivrs?status=needs_review',
  ],
  ['Open shipments', 'openShipments', 'package_2', '/distributor/shipments'],
];
const statuses = new Set(['submitted', 'approved', 'shipped', 'completed', 'cancelled']);
const unwrap = (response) => response?.data?.data;
const validOrders = (value) =>
  Array.isArray(value?.data) &&
  Number.isSafeInteger(value.total) &&
  value.total >= value.data.length &&
  value.data.length <= 20 &&
  new Set(value.data.map((row) => row?._id)).size === value.data.length &&
  value.data.every(
    (row) =>
      typeof row?._id === 'string' &&
      row._id.length > 0 &&
      typeof row.orderId === 'string' &&
      row.orderId.length > 0 &&
      statuses.has(row.status) &&
      Number.isFinite(Date.parse(row.createdAt))
  );

export default function DistributorDashboard({ t, refresh }) {
  const locale = useLocale();
  const { begin, version } = useShippingRequest('distributor-dashboard');
  const { begin: beginDetail, version: detailVersion } =
    useShippingRequest('overview-order-detail');
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [bank, setBank] = useState(null);
  const [bankError, setBankError] = useState(false);
  const [orders, setOrders] = useState(null);
  const [ordersError, setOrdersError] = useState(false);
  const [detail, setDetail] = useState(null);
  const [opening, setOpening] = useState(null);
  const [detailError, setDetailError] = useState(false);
  useEffect(() => {
    let active = true;
    const request = begin();
    const current = () => active && request.isCurrent();
    setOverview(null);
    setError(false);
    setBank(null);
    setBankError(false);
    setOrders(null);
    setOrdersError(false);
    // Each read settles independently: a failed balance must not hide order activity.
    void (async () => {
      try {
        const value = unwrap(await distributorService.getDashboard());
        if (
          !value ||
          metrics.some(([, key]) => !Number.isSafeInteger(value[key]) || value[key] < 0)
        )
          throw new Error('Invalid dashboard');
        if (current()) setOverview(value);
      } catch {
        if (current()) setError(true);
      }
    })();
    void (async () => {
      try {
        const value = unwrap(
          await distributorService.cashBalance(undefined, { signal: request.signal })
        );
        if (!validBalance(value)) throw new Error('Invalid balance');
        if (current()) setBank(value);
      } catch {
        if (!current()) return;
        setBankError(true);
      }
    })();
    void (async () => {
      try {
        const value = unwrap(await distributorService.listOrders(1));
        if (!validOrders(value)) throw new Error('Invalid recent orders');
        if (current()) setOrders(value.data.slice(0, 5));
      } catch {
        if (current()) setOrdersError(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [begin, version, refresh, retry]);
  useEffect(() => {
    setDetail(null);
    setOpening(null);
    setDetailError(false);
  }, [detailVersion]);
  const openOrder = async (id) => {
    const request = beginDetail();
    setOpening(id);
    setDetail(null);
    setDetailError(false);
    try {
      const value = unwrap(await distributorService.getOrder(id));
      if (value?._id !== id) throw new Error('Invalid order');
      if (request.isCurrent()) setDetail(value);
    } catch {
      if (request.isCurrent()) setDetailError(true);
    } finally {
      if (request.isCurrent()) setOpening(null);
    }
  };
  const closeDetail = () => {
    beginDetail();
    setDetail(null);
    setOpening(null);
  };
  return (
    <section aria-label={t('Distributor overview')} className="me-overview">
      <div className="me-overview-toolbar">
        <button type="button" onClick={() => setRetry((value) => value + 1)} className="me-dw-link">
          <span className="material-symbols-outlined" aria-hidden="true">
            refresh
          </span>
          {t('Refresh overview')}
        </button>
      </div>
      <div className="me-overview-summary">
        <section className="me-overview-bank" aria-label={t('Bank summary')}>
          <header className="me-overview-panel-heading">
            <h2>{t('Virtual Tissue Bank Balance')}</h2>
            <span className="material-symbols-outlined" aria-hidden="true">
              account_balance_wallet
            </span>
          </header>
          <div className="me-overview-bank-body">
            {bankError ? (
              <p role="status">{t('Balance unavailable. Refresh to try again.')}</p>
            ) : !bank ? (
              <p role="status">{t('Loading balance…')}</p>
            ) : (
              <>
                <p className="me-overview-muted">
                  {t(bank.spendingBlocked ? 'Recorded balance' : 'Available balance')} · USD
                </p>
                <strong className="me-overview-money">{usd(bank.balanceCents)}</strong>
                {bank.spendingBlocked && (
                  <p className="me-overview-warning" role="status">
                    {t('Spending is paused. Review your tissue bank.')}
                  </p>
                )}
              </>
            )}
            <Link to="/distributor/bank" className="me-overview-bank-link">
              {t('Open tissue bank')}
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </section>
        <div className="me-overview-metrics">
          {error && <p role="alert">{t('Overview is unavailable. Please try again.')}</p>}
          {!overview && !error && <p role="status">{t('Loading overview...')}</p>}
          {overview &&
            metrics.map(([label, key, icon, to]) => (
              <Link
                to={to}
                key={key}
                className={`me-overview-metric${key === 'needsReviewIvrs' && overview[key] > 0 ? ' needs-attention' : ''}`}
              >
                <div>
                  <span className="me-overview-metric-label">{t(label)}</span>
                  <strong>{overview[key].toLocaleString(locale)}</strong>
                </div>
                <span className="material-symbols-outlined" aria-hidden="true">
                  {icon}
                </span>
              </Link>
            ))}
        </div>
      </div>
      <div className="me-overview-content">
        <section className="me-overview-panel" aria-label={t('Recent orders')}>
          <header className="me-overview-panel-heading">
            <h2>{t('Recent orders')}</h2>
            <Link to="/distributor/orders" className="me-dw-link">
              {t('View all orders')} <span aria-hidden="true">→</span>
            </Link>
          </header>
          {ordersError && (
            <p className="me-overview-empty" role="status">
              {t('Recent orders are unavailable. Refresh to try again.')}
            </p>
          )}
          {!orders && !ordersError && (
            <p className="me-overview-empty" role="status">
              {t('Loading orders…')}
            </p>
          )}
          {orders?.length === 0 && (
            <div className="me-overview-empty">
              <p>{t('No orders yet.')}</p>
              <Link to="/distributor/orders" className="me-dw-link">
                {t('Go to Orders')}
              </Link>
            </div>
          )}
          {detailError && (
            <p className="me-overview-empty" role="alert">
              {t('Could not open this record. Please try again.')}
            </p>
          )}
          {orders?.length > 0 && (
            <div
              className="me-overview-table-scroll"
              tabIndex={0}
              role="region"
              aria-label={t('Recent orders table')}
            >
              <table>
                <thead>
                  <tr>
                    {['Order', 'Customer', 'Status', 'Submitted'].map((label) => (
                      <th key={label} scope="col">
                        {t(label)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order._id}>
                      <td>
                        <button
                          type="button"
                          className="me-dw-link"
                          onClick={() => openOrder(order._id)}
                          disabled={!!opening}
                          aria-label={t('View order {id}', { id: order.orderId })}
                        >
                          {order.orderId}
                        </button>
                        {order.patientName && (
                          <small>
                            {t('Customer PO')}: {order.patientName}
                          </small>
                        )}
                      </td>
                      <td>{order.clinic?.name || '—'}</td>
                      <td>
                        <StatusBadge status={order.status} size="small" />
                        {distributorHoldLabel(order) && (
                          <small className="me-overview-warning">
                            {t(distributorHoldLabel(order))}
                          </small>
                        )}
                      </td>
                      <td className="me-overview-date">
                        {new Date(order.createdAt).toLocaleDateString(locale, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <DistributorOperationalNotices compact refresh={retry} />
      </div>
      <DistributorOverviewActivity
        t={t}
        refresh={`${refresh}:${retry}`}
        onOpenOrder={openOrder}
        opening={opening}
      />
      {detail && (
        <OrderDetailsModal
          key={detail._id}
          order={detail}
          canAdminister={false}
          onClose={closeDetail}
          onAction={() => {
            closeDetail();
            setRetry((value) => value + 1);
          }}
        />
      )}
    </section>
  );
}
